import { Control, FieldErrors, UseFormSetValue } from 'react-hook-form';
import { StudentProfileForm } from '../../../../constants/types/student-profile-form';
import FormRow from '../../../common/input/FormRow';
import { emailRules, phoneRules } from '../../../../constants/validation';
import { Field } from '../../../common/form/Field';
import SectionHeader, {
  SectionActionProps,
} from '../../../common/form/SectionHeader';
import { useAddressSearch } from '../../../../hooks/useAddressSearch';

/**
 * LMS > 학생 관리 > 학생 상세 정보 > 추가 정보 영역.
 * @description
 * - 선생님이 학생의 추가 정보를 대신 수정합니다.
 * - 선생님이 대신 입력하는 화면이라 모두 선택 입력이고, 값이 있을 때만 형식을 검사합니다.
 */
export const ExtraInfoSection = ({
  control,
  errors,
  section,
  setValue,
}: {
  control: Control<StudentProfileForm>;
  errors: FieldErrors<StudentProfileForm>;
  section: SectionActionProps;
  /** 주소 검색 결과를 폼에 반영하기 위해 필요하다. */
  setValue: UseFormSetValue<StudentProfileForm>;
}) => {
  const isEditing = section.isEditing;

  const openAddressSearch = useAddressSearch(({ address, zipCode }) => {
    setValue('address', address);
    setValue('zipCode', zipCode);
    // 이전 주소의 상세주소가 새 주소에 그대로 붙지 않도록 함께 비운다.
    setValue('detailAddress', '');
  });

  return (
    <section className="mt-6">
      <SectionHeader title="추가 정보" {...section} />
      <div className="mt-5 grid grid-cols-1 gap-x-4 pb-2 xl:grid-cols-2">
        <Field label="전화번호" error={errors.phone?.message}>
          <FormRow
            type="phone"
            name="phone"
            control={control}
            placeholder="- 없이 번호만 입력"
            rules={phoneRules()}
            error={!!errors.phone}
            disabled={!isEditing}
            autoComplete="off"
          />
        </Field>

        <Field label="이메일" error={errors.email?.message}>
          <FormRow
            type="text"
            name="email"
            control={control}
            placeholder="이메일 주소 입력"
            rules={emailRules()}
            error={!!errors.email}
            disabled={!isEditing}
            autoComplete="off"
          />
        </Field>

        <Field label="학교">
          <FormRow
            type="text"
            name="schoolName"
            control={control}
            placeholder="학교 입력"
            disabled={!isEditing}
            autoComplete="off"
          />
        </Field>

        <Field label="학교 반">
          <FormRow
            type="text"
            name="schoolGroup"
            control={control}
            placeholder="학교 반 입력"
            disabled={!isEditing}
            autoComplete="off"
          />
        </Field>

        <Field label="주소">
          <FormRow
            type="text-button"
            name="address"
            control={control}
            placeholder="주소 검색으로 입력"
            textButtonProp={{
              text: '주소 검색',
              action: () => {
                if (!isEditing) return;
                openAddressSearch();
              },
              disabled: !isEditing,
            }}
            disabled
          />
        </Field>

        <Field label="상세주소">
          <FormRow
            type="text"
            name="detailAddress"
            control={control}
            placeholder="상세주소 입력"
            disabled={!isEditing}
            autoComplete="off"
          />
        </Field>

        {/* 우편번호(zipCode)는 주소 검색으로만 채워지고 직접 고칠 수 없어 화면에 노출하지 않는다.
            폼 값과 저장 대상(SECTION_FIELDS.extra)에는 그대로 포함된다. */}
      </div>
    </section>
  );
};
